import React, { useState, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { css } from '@emotion/core'
import { IdentityVerifyUploadSection } from '@/components/organisms/self-checkin/identity-verify/identity-verify-upload-section'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import { IdentityVerifyCamera } from '@/components/organisms/self-checkin/identity-verify/identity-verify-camera'
import { IdentityDocumentCaptureItem } from '@/components/organisms/self-checkin/identity-verify/identity-document-capture-item'
import {
  getIdentificationRequirement,
  hasRequiredJapaneseIdentityDocumentImages,
  IdentificationRequirement,
  isJapaneseIdentityDocumentBackRequired,
} from '@/libs/self-checkin'
import { JapaneseIdentityDocumentType } from '@/models/self-checkin/identification'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'
import { FrontTabletRadioButton } from '@/components/molecules/front-tablet/input/radio-button'
import { createSelfCheckinResponsiveStyle } from '@/utils/front-tablet-responsive-style'

type ContainerProps = {
  title: string
}

const SampleImageByJapaneseIdentityDocumentType: Record<JapaneseIdentityDocumentType, { front: string; back?: string }> = {
  [JapaneseIdentityDocumentType.DriversLicense]: {
    front: require('@/static/images/self-checkin/drivers-license-front-sample.svg'),
    back: require('@/static/images/self-checkin/drivers-license-back-sample.svg'),
  },
  [JapaneseIdentityDocumentType.MyNumberCard]: {
    front: require('@/static/images/self-checkin/my-number-card-front-sample.svg'),
  },
  [JapaneseIdentityDocumentType.ResidenceCard]: {
    front: require('@/static/images/self-checkin/residence-card-front-sample.svg'),
    back: require('@/static/images/self-checkin/residence-card-back-sample.svg'),
  },
}

export const UploadLicense: React.FC<ContainerProps> = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const { state, saveState, stateErrorHandling } = useSelfCheckInState()
  const [activateCameraType, setActivateCameraType] = useState<'front' | 'back' | undefined>(undefined)
  const [documentType, setDocumentType] = useState<JapaneseIdentityDocumentType>(JapaneseIdentityDocumentType.DriversLicense)
  const [identificationFrontImage, setIdentificationFrontImage] = useState<string>()
  const [identificationFrontObjectKey, setIdentificationFrontObjectKey] = useState<string>()
  const [identificationBackImage, setIdentificationBackImage] = useState<string>()
  const [identificationBackObjectKey, setIdentificationBackObjectKey] = useState<string>()
  const [isInitializeComponent, setIsInitializeComponent] = useState(false)

  const documentTypeOptions = [
    { value: JapaneseIdentityDocumentType.DriversLicense, label: t("Driver's License") },
    { value: JapaneseIdentityDocumentType.MyNumberCard, label: t('My Number Card') },
    { value: JapaneseIdentityDocumentType.ResidenceCard, label: t('Residence Card') },
  ]

  const captureSides: Array<'front' | 'back'> = isJapaneseIdentityDocumentBackRequired(documentType) ? ['front', 'back'] : ['front']

  const changeDocumentType = (nextDocumentType: JapaneseIdentityDocumentType) => {
    setDocumentType(nextDocumentType)
    setIdentificationFrontImage(undefined)
    setIdentificationFrontObjectKey(undefined)
    setIdentificationBackImage(undefined)
    setIdentificationBackObjectKey(undefined)
  }

  const isDisabled = (): boolean =>
    !hasRequiredJapaneseIdentityDocumentImages({
      documentType,
      identificationFrontImage,
      identificationBackImage,
    })

  const onShutter = (image: any, nationality: string | undefined, objectKey: string) => {
    if (activateCameraType === 'front') {
      setIdentificationFrontImage(image)
      setIdentificationFrontObjectKey(objectKey)
    } else {
      setIdentificationBackImage(image)
      setIdentificationBackObjectKey(objectKey)
    }
    setActivateCameraType(undefined)
  }

  const submit = async () => {
    saveState({
      ...state,
      uploadLicense: {
        nationality: undefined,
        documentType,
        identificationFrontImage,
        identificationFrontObjectKey,
        identificationBackImage,
        identificationBackObjectKey,
      },
    })
    history.push({ pathname: '/self-checkin/confirm' })
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    if (!stateErrorHandling(state, 'setting')) {
      return
    }

    if (getIdentificationRequirement(state, state.accommodationInfo?.nationality) === IdentificationRequirement.None) {
      history.replace({ pathname: '/self-checkin/confirm' })
      return
    }
    setIsInitializeComponent(true)
    if (state.uploadLicense) {
      setDocumentType(state.uploadLicense.documentType ?? JapaneseIdentityDocumentType.DriversLicense)
      setIdentificationFrontImage(state.uploadLicense.identificationFrontImage)
      setIdentificationFrontObjectKey(state.uploadLicense.identificationFrontObjectKey)
      setIdentificationBackImage(state.uploadLicense.identificationBackImage)
      setIdentificationBackObjectKey(state.uploadLicense.identificationBackObjectKey)
    }
    // 表示時の初期化（遷移先の判定と、保存済みの種類・画像の復元）なので、マウント時に1回だけ実行する
  }, [])

  if (!isInitializeComponent) {
    return <></>
  }

  return (
    <SelfCheckinLayout
      state={state}
      fullscreen={Boolean(activateCameraType)}
      header={!activateCameraType ? { goToPreviousPage: history.goBack, title: t('Identification') } : undefined}
      footer={!activateCameraType ? { right: { isDisabled: isDisabled(), label: t('Next'), onClick: submit } } : undefined}
    >
      {!activateCameraType && (
        <IdentityVerifyUploadSection
          type="license"
          heading={t('Please take a photo of your ID for identity verification')}
          notice={
            <Trans i18nKey="As this facility operates unstaffed, we verify your identity as required by law. Uploaded images are used only for identity verification, are properly managed by this facility and its contractors, and are completely deleted after verification." />
          }
        >
          <div css={documentTypeListStyle}>
            {documentTypeOptions.map(option => (
              <FrontTabletRadioButton
                key={option.value}
                id={`identity-document-type-${option.value}`}
                label={option.label}
                formValue={documentType}
                name="identityDocumentType"
                onChange={() => changeDocumentType(option.value)}
                value={option.value}
                radioSizeType="small"
                radioLabelSizeType="small"
              />
            ))}
          </div>
          <div css={uploadWrapperStyle}>
            {captureSides.map(side => (
              <IdentityDocumentCaptureItem
                key={side}
                type="license"
                image={side === 'front' ? identificationFrontImage : identificationBackImage}
                sampleImage={SampleImageByJapaneseIdentityDocumentType[documentType][side]}
                label={side === 'front' ? t('Front of the card') : t('Back of the card')}
                onCapture={() => setActivateCameraType(side)}
              />
            ))}
          </div>
        </IdentityVerifyUploadSection>
      )}
      {activateCameraType && (
        <IdentityVerifyCamera
          onCancel={() => setActivateCameraType(undefined)}
          onShutter={onShutter}
          type="license"
          side={activateCameraType}
        />
      )}
    </SelfCheckinLayout>
  )
}

const documentTypeListStyle = css({
  display: 'flex',
  gap: 32,
  marginTop: 40,
})

const uploadWrapperStyle = css(
  {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: 42,
    marginTop: 40,
  },
  createSelfCheckinResponsiveStyle({
    portrait: { columnGap: 34 },
    landscape13: { columnGap: 40 },
  }),
)
